// materialSlotOwner — which material_mapping row speaks for a mesh NAME when
// several rows share it.
//
// Pure + dependency-free so it stays unit-testable (avatarMaterial.js imports
// Vite-resolved specifiers and cannot be loaded under node --test).
//
// The server writes one mapping row per glTF PRIMITIVE, so a mesh with several
// materials — a MetaHuman face is one mesh with nine primitives: skin, teeth,
// eyes, lashes... — arrives as nine rows with the same meshName and
// primitiveIndex 0..8. The runtime binds by node name, and today it keys its
// material map by meshName alone, so the LAST row won: the face's skin (the node
// that carries the bare name) wore the ninth row's material — cartilage, which is
// alpha-masked, so the head vanished. Observed on eng1010-smoke6-step0.
//
// Until the runtime joins rows to primitives structurally (that is a separate,
// flagged change), one row has to own the bare name, and the right one is the
// row with the LOWEST primitiveIndex: three.js gives the bare name to primitive
// 0's object and suffixes the rest. Ordering is decided here, not by which row
// happened to come last in the array — a client may PUT rows in any order.
//
// Inert for every existing character: with exactly one row per name (which is
// every shipped rig — measured maxPrimitiveIndex 0 across staging and prod),
// the owner is that row.

/**
 * @param {Array<{meshName?: string, primitiveIndex?: number}>} mapping
 * @returns {Map<string, object>} meshName → the row that owns the bare name
 */
export function pickBareNameOwners(mapping) {
  const owners = new Map()
  for (const entry of mapping ?? []) {
    if (!entry || typeof entry.meshName !== 'string') continue
    const prim = Number.isInteger(entry.primitiveIndex) ? entry.primitiveIndex : 0
    const current = owners.get(entry.meshName)
    if (!current) { owners.set(entry.meshName, entry); continue }
    const currentPrim = Number.isInteger(current.primitiveIndex) ? current.primitiveIndex : 0
    // Strictly lower wins; on a tie the first seen keeps it, so a duplicated
    // (meshName, primitiveIndex) pair — which the server now rejects on write —
    // still resolves deterministically if an old mapping carries one.
    if (prim < currentPrim) owners.set(entry.meshName, entry)
  }
  return owners
}
